/**
 * InviteUser.tsx — the "Create user" invite wizard (ADMIN only).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.5 (clinician + admin).
 *
 * WHY a stepped wizard (CTO direction, 2026-07-16): "don't you need a first
 * name and last name to create the account? It should be: create user → get
 * the name → get the email address → determine the account type → based on
 * that, the next set of fields. Keep the title fixed." So this is one Drawer
 * (frozen header, FIXED title "Create user") holding a progressive wizard:
 *   1. Name (first + last — joined as `fullName` for the API; identity now
 *      REQUIRES fullName on invites for BOTH roles),
 *   2. Email,
 *   3. Account type (Admin | Clinician cards),
 *   4. Clinician only: prescriber type + registration number (the label
 *      switches GMC / Registration number / GPhC — matching the prescription
 *      PDF). An Admin invite skips straight to review,
 *   5. Review + Create invite.
 * Back/Continue live in the Drawer's pinned footer; each step gates Continue
 * client-side (empty fields / a '@'-less email never advance), and a server
 * 400/409 still surfaces inline on the review step (e.g. email already
 * registered as a clinician).
 *
 * Accounts stay invite-only (no admin-set passwords, no self-elevation): the
 * identity service returns a single-use token ONCE — shown on success so the
 * admin can pass the link to the invitee (who opens it to set their own
 * password) alongside the "invite email sent" line. `onInvited` fires right
 * after a successful invite so the caller can refetch the staff table (the
 * new pending invite appears) while the drawer keeps showing the link.
 *
 * This component OWNS the Drawer (the footer actions need the wizard state);
 * UsersTab mounts it only while open, so every open starts a fresh wizard.
 *
 * No money on this surface.
 */
import { useState } from 'react';
import { ApiError } from '../api/client';
import { createInvite, searchStaffUserByEmail } from '../api/identity';
import type { InviteCreated, PrescriberType } from '../api/types';
import { formatUtcDate } from '../lib/format';
import { buildInviteLink, CopyLinkButton } from '../staff/ui';
import { Alert, Button, Drawer, ErrorLine, Field, Help, OptionButton, Select, TextInput } from '../ui/kit';

/** Matches identity `AdminService.createInvite` ConflictException copy. */
const CLINICIAN_EMAIL_TAKEN = 'This email is already registered as a clinician';

const PRESCRIBER_TYPES: Array<{ value: PrescriberType; label: string }> = [
  { value: 'doctor', label: 'Doctor' },
  { value: 'independent_prescriber', label: 'Independent prescriber' },
  { value: 'pharmacist', label: 'Pharmacist' },
];

/** The registration-number field's label depends on the prescriber type (prescription-PDF wording). */
const REGISTRATION_LABEL: Record<PrescriberType, string> = {
  doctor: 'GMC number',
  independent_prescriber: 'Registration number',
  pharmacist: 'GPhC number',
};

type StaffRole = 'ADMIN' | 'CLINICIAN';

/** The account-type cards — a one-line "what can this account do" each. */
const ROLE_CARDS: Array<{ role: StaffRole; label: string; description: string }> = [
  { role: 'ADMIN', label: 'Admin', description: 'Runs the console — plans and pricing, users, orders and billing. No clinical review.' },
  { role: 'CLINICIAN', label: 'Clinician', description: 'Reviews submitted assessments and issues prescriptions — needs a prescriber registration number.' },
];

/**
 * The address as it will be STORED. Lower-cased as well as trimmed — see the
 * comment on the email input for the lockout this prevents. Applied at every
 * boundary that leaves this component, not just at the keystroke, so an autofill
 * or a later edit cannot reintroduce the capital.
 */
const inviteEmail = (raw: string): string => raw.trim().toLowerCase();

type WizardStep = 'name' | 'email' | 'type' | 'clinician' | 'review';

/**
 * The step order for a role. An ADMIN invite has no record fields, so it skips
 * the clinician step; until a type is chosen the longer path is shown (the
 * step counter can only ever shrink, never jump up mid-flow).
 */
function stepsFor(role: StaffRole | null): WizardStep[] {
  return role === 'ADMIN'
    ? ['name', 'email', 'type', 'review']
    : ['name', 'email', 'type', 'clinician', 'review'];
}

export function InviteUser({ onClose, onInvited }: { onClose: () => void; onInvited?: () => void }) {
  const [step, setStep] = useState<WizardStep>('name');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<StaffRole | null>(null);
  const [prescriberType, setPrescriberType] = useState<PrescriberType>('doctor');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<InviteCreated | null>(null);

  const steps = stepsFor(role);
  const stepIndex = steps.indexOf(step);
  /** The API's display name — the two fields joined ("First Last"). */
  const fullName = `${firstName.trim()} ${lastName.trim()}`;

  /** Per-step gate — Continue never advances past an empty/invalid field. */
  function validate(current: WizardStep): string | null {
    if (current === 'name' && (!firstName.trim() || !lastName.trim())) return 'Enter a first name and a last name.';
    if (current === 'email' && !email.includes('@')) return 'Enter a valid email address.';
    if (current === 'type' && !role) return 'Choose an account type.';
    if (current === 'clinician' && !registrationNumber.trim()) {
      return `Enter the ${REGISTRATION_LABEL[prescriberType]} — it goes on the prescription PDF.`;
    }
    return null;
  }

  async function handleContinue() {
    const problem = validate(step);
    if (problem) {
      setError(problem);
      return;
    }
    // When the admin enters the clinician's email, reject early if that address
    // already belongs to a CLINICIAN account (identity enforces the same rule
    // at invite create — this just fails before the rest of the wizard).
    if (step === 'email') {
      setBusy(true);
      setError(null);
      try {
        const { user } = await searchStaffUserByEmail(inviteEmail(email));
        if (user?.role === 'CLINICIAN') {
          setError(CLINICIAN_EMAIL_TAKEN);
          return;
        }
      } catch {
        // Search is best-effort UX — createInvite still enforces the rule.
      } finally {
        setBusy(false);
      }
    }
    setError(null);
    setStep(steps[stepIndex + 1]);
  }

  /** Back never clears anything — every field keeps its value for re-editing. */
  function handleBack() {
    setError(null);
    setStep(steps[Math.max(0, stepIndex - 1)]);
  }

  async function handleCreate() {
    if (busy || !role) return;
    setBusy(true);
    setError(null);
    try {
      const result = await createInvite({
        email: inviteEmail(email),
        role,
        // Identity now REQUIRES fullName for BOTH roles — always sent.
        fullName,
        ...(role === 'CLINICIAN' ? { registrationNumber: registrationNumber.trim(), prescriberType } : {}),
      });
      setCreated(result);
      onInvited?.();
    } catch (err) {
      if (err instanceof ApiError && err.detail) setError(err.detail);
      else setError('Sorry — we could not create the invite. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  /** The full single-use activation link the invitee opens to set their password. */
  const inviteLink = created ? buildInviteLink(created.token) : '';

  // The primary action lives in the Drawer's pinned footer (never scrolls away
  // under a long step). After success there is nothing left to submit — just Done.
  const footer = created ? (
    <Button type="button" variant="primary" onClick={onClose}>
      Done
    </Button>
  ) : (
    <div style={{ display: 'flex', gap: 'var(--gps-s-3)' }}>
      {stepIndex > 0 ? (
        <Button type="button" variant="ghost" onClick={handleBack} disabled={busy}>
          Back
        </Button>
      ) : null}
      {step === 'review' ? (
        <Button type="button" variant="cta" onClick={handleCreate} disabled={busy}>
          {busy ? 'Creating invite…' : 'Create invite'}
        </Button>
      ) : (
        <Button type="button" variant="primary" onClick={() => void handleContinue()} disabled={busy}>
          {busy && step === 'email' ? 'Checking…' : 'Continue'}
        </Button>
      )}
    </div>
  );

  return (
    <Drawer open onClose={onClose} title="Create user" width="lg" footer={footer}>
      {created ? (
        <Alert tone="success" role="status" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
          <span>
            Invite created for <strong>{created.email}</strong> ({created.role.toLowerCase()}), expiring{' '}
            {formatUtcDate(created.expiresAt) || 'soon'}. The invite email has been sent — if you need it, this
            single-use link opens the same set-password screen:
          </span>
          <code style={{ display: 'block', wordBreak: 'break-all', background: 'var(--gps-surface)', color: 'var(--gps-text)', padding: '8px 10px', borderRadius: 'var(--gps-r-sm)', margin: '8px 0', fontSize: '13px' }}>
            {inviteLink}
          </code>
          <CopyLinkButton value={inviteLink} label="Copy invite link" style={{ maxWidth: '12rem' }} />
        </Alert>
      ) : (
        <>
          <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--gps-muted)', margin: '0 0 4px', fontVariantNumeric: 'tabular-nums' }}>
            Step {stepIndex + 1} of {steps.length}
          </p>

          {step === 'name' ? (
            <>
              <StepHeading>Who is this account for?</StepHeading>
              <Help>Their name appears in the staff directory and on anything they issue.</Help>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gps-s-3)' }}>
                <Field label="First name" htmlFor="invite-firstName">
                  <TextInput id="invite-firstName" autoComplete="off" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                </Field>
                <Field label="Last name" htmlFor="invite-lastName">
                  <TextInput id="invite-lastName" autoComplete="off" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </Field>
              </div>
            </>
          ) : null}

          {step === 'email' ? (
            <>
              <StepHeading>What&rsquo;s their email address?</StepHeading>
              <Help>The invite email goes here — it becomes their sign-in email.</Help>
              <Field label="Email" htmlFor="invite-email">
                {/* ⚠️ LOWER-CASED AT THE KEYSTROKE, AND THIS IS A REAL INCIDENT
                    FIX (CTO, 2026-09-03: "make this email field lowercase only
                    then we can solve this").

                    Two ADMINs were locked out of production for days because
                    their stored address carried a capital first letter, and the
                    login lookup is a case-sensitive `findUnique` on
                    `(tenantId, email)`. Identity now falls back to a
                    case-insensitive read (identity #83), but that is a rescue —
                    this is the prevention.

                    Shakila, who created both accounts, was certain she typed
                    them in lower case and that they "changed to upper case after
                    a couple of days". She was right about both halves:
                      • this input had NO `autoCapitalize`, so a mobile keyboard
                        (and some autofill) capitalises the first letter silently
                        — `type="email"` suppresses it on iOS Safari but NOT
                        reliably elsewhere;
                      • and the `User` row is not created at invite time at all.
                        `acceptInvite` creates it from `InviteToken.email` when
                        the person clicks the link — which is why the capital
                        "appeared" days later.
                    So the value is normalised HERE, at the only point a human
                    types it, rather than trusted downstream.

                    `autoCapitalize`/`autoCorrect`/`spellCheck` are belt-and-braces
                    for the keyboard; the `toLowerCase()` is what actually
                    guarantees it, including for a pasted capitalised address. */}
                <TextInput
                  id="invite-email"
                  type="email"
                  autoComplete="off"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value.toLowerCase())}
                />
              </Field>
            </>
          ) : null}

          {step === 'type' ? (
            <>
              <StepHeading>What type of account?</StepHeading>
              <Help>This decides what they can see and do in the portal.</Help>
              {ROLE_CARDS.map((card) => (
                <OptionButton
                  key={card.role}
                  selected={role === card.role}
                  description={card.description}
                  onClick={() => {
                    setRole(card.role);
                    setError(null);
                  }}
                >
                  {card.label}
                </OptionButton>
              ))}
            </>
          ) : null}

          {step === 'clinician' ? (
            <>
              <StepHeading>Clinician details</StepHeading>
              <Help>These go on the prescription PDF, so they must match the register.</Help>
              <Field label="Prescriber type" htmlFor="invite-prescriberType">
                <Select
                  id="invite-prescriberType"
                  value={prescriberType}
                  onChange={(e) => setPrescriberType(e.target.value as PrescriberType)}
                >
                  {PRESCRIBER_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </Select>
              </Field>
              <Field label={REGISTRATION_LABEL[prescriberType]} htmlFor="invite-registrationNumber">
                <TextInput id="invite-registrationNumber" value={registrationNumber} onChange={(e) => setRegistrationNumber(e.target.value)} />
              </Field>
            </>
          ) : null}

          {step === 'review' ? (
            <>
              <StepHeading>Check and create</StepHeading>
              <Help>The invite email is sent as soon as you create it.</Help>
              <dl style={{ margin: 0 }}>
                <ReviewRow term="Name" value={fullName} />
                <ReviewRow term="Email" value={inviteEmail(email)} />
                <ReviewRow term="Account type" value={role === 'ADMIN' ? 'Admin' : 'Clinician'} />
                {role === 'CLINICIAN' ? (
                  <>
                    <ReviewRow term="Prescriber type" value={PRESCRIBER_TYPES.find((t) => t.value === prescriberType)?.label ?? prescriberType} />
                    <ReviewRow term={REGISTRATION_LABEL[prescriberType]} value={registrationNumber.trim()} />
                  </>
                ) : null}
              </dl>
            </>
          ) : null}

          {error ? <ErrorLine>{error}</ErrorLine> : null}
        </>
      )}
    </Drawer>
  );
}

/** One wizard-step heading (the Drawer's own h2 title stays fixed above). */
function StepHeading({ children }: { children: string }) {
  return <h3 style={{ margin: '0 0 6px', fontSize: '17px', color: 'var(--gps-text)' }}>{children}</h3>;
}

/** One review summary row (term left, entered value right). */
function ReviewRow({ term, value }: { term: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--gps-s-3)', padding: '10px 0', borderBottom: '1px solid var(--gps-border)' }}>
      <dt style={{ margin: 0, fontSize: '12.5px', fontWeight: 600, color: 'var(--gps-muted)' }}>{term}</dt>
      <dd style={{ margin: 0, fontSize: '14px', color: 'var(--gps-text)', textAlign: 'right' }}>{value}</dd>
    </div>
  );
}
